/**
 * Open editors, the mega-menu way: a drop-down button with a count badge in
 * the HOST's top bar (published through `setTopBarSlots`), and the panel it
 * drops, drawn by the layout under the button. The panel is the shared
 * `EditorsPanel`; this file only places it and owns its open/close rules
 * (Esc and outside click close it, focus returns to the button).
 */
import { Pencil } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { Z_TOP_BAR } from '../../../layers'
import { tw } from '../../../testHooks'
import { EditorsPanel } from '../../nav/parts/Notices'
import { S } from '../../nav/tokens'
import type { ShellNavModel } from '../../nav/useShellNav'

export function EditorsButton({ btnRef, count, dirty, open, onToggle, popupId }: {
  btnRef: RefObject<HTMLButtonElement | null>
  count: number
  dirty: number
  open: boolean
  onToggle: () => void
  popupId: string
}) {
  const label = `Open editors, ${count} open${dirty ? `, ${dirty} with unsaved changes` : ''}`
  return (
    <button
      ref={btnRef as RefObject<HTMLButtonElement>}
      type="button"
      // `tw-tb`: the host bar's own button look (its CSS ships with the bar).
      className="tw-tb tw-mega-ed"
      onClick={onToggle}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? popupId : undefined}
      aria-label={label}
      title="Open editors"
      {...tw('action', 'shell-mega-editors', { count, expanded: open })}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, height: 38, padding: '0 10px',
        border: '1px solid transparent', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
        fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap',
        background: open ? S.surface2 : 'transparent', color: S.text,
      }}
    >
      <Pencil size={16} aria-hidden />
      Open editors
      {/* Amber while anything is open — the same amber as the unsaved dot, so
          "work in progress" reads the same everywhere; dark ink on it in both
          themes. */}
      <span aria-hidden style={{
        minWidth: 22, padding: '1px 7px', borderRadius: 10, textAlign: 'center',
        fontSize: 13, fontWeight: 600, lineHeight: '18px',
        background: count ? S.unsaved : S.surface2, color: count ? '#1a1200' : S.text2,
      }}>
        {count}
      </span>
    </button>
  )
}

export function EditorsPopover({ model, btnRef, popupId, onClose }: {
  model: ShellNavModel
  btnRef: RefObject<HTMLButtonElement | null>
  popupId: string
  /** `refocus`: return focus to the button (Esc), not after a pick. */
  onClose: (refocus: boolean) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)

  // Under the button, right-aligned to it, kept on screen.
  useLayoutEffect(() => {
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect()
      if (!r) { setPos({ top: 64, right: 12 }); return }
      setPos({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right) })
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [btnRef])

  // Focus moves in ONCE, on opening — not on every re-render while it is open
  // (an editor's dirty flag changing must not yank focus back to the top).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus()
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (ref.current?.contains(t) || btnRef.current?.contains(t)) return
      onCloseRef.current(false)
    }
    // Esc closes it wherever focus is — an empty list leaves focus on the
    // button, outside the panel.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      e.preventDefault()
      onCloseRef.current(true)
    }
    window.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onDown)
      window.removeEventListener('keydown', onKey)
    }
  }, [btnRef])

  return (
    <div
      ref={ref}
      id={popupId}
      role="dialog"
      aria-label="Open editors"
      {...tw('action', 'shell-mega-editors-panel')}
      onBlur={(e) => {
        const next = e.relatedTarget as Node | null
        if (next && !ref.current?.contains(next) && !btnRef.current?.contains(next)) onClose(false)
      }}
      style={{
        position: 'fixed', top: pos?.top ?? 64, right: pos?.right ?? 12, zIndex: Z_TOP_BAR + 20,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      <EditorsPanel model={model} onClose={() => onClose(false)} />
    </div>
  )
}
